import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import StandardModal from './StandardModal';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarPicker } from './ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { 
  AlertCircle, Package, User, Phone, Mail, Users,
  MapPin, Calendar, CalendarDays, Sun, DollarSign, Gift, FileText, CreditCard,
  Loader2, Check, Clock, Send, Anchor, AlertTriangle, ChevronDown, Plus, Trash2, XCircle, Bell
} from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './Buttons';
import TimeSlotSelector from './TimeSlotSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import ReconcilePaymentSection from './ReconcilePaymentSection';
import { toast } from 'sonner';
import api from '../utils/api';
import apiV2 from '../utils/apiV2';
import { invalidateFinancials } from '../hooks/useFinancials';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import useMinChargeFloor from '../hooks/useMinChargeFloor';
import { TIMEZONES } from '../utils/timezones';
import TeamAssignmentSelector from './TeamAssignmentSelector';
import { buildTeamSlotsFromAsset, getTeamMembersWithStatus } from '../utils/teamAssignments';

/**
 * Edit Booking Modal - Clean, organized layout
 * Tabs: Trip | Customer | Assignment | Payment | Extras
 */

const EMPTY_ARRAY = [];

// Duration options in minutes
const DURATION_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 90, label: '1.5 hr' },
  { value: 120, label: '2 hr' },
  { value: 150, label: '2.5 hr' },
  { value: 180, label: '3 hr' },
  { value: 240, label: '4 hr' },
  { value: 300, label: '5 hr' },
  { value: 360, label: '6 hr' },
  { value: 480, label: '8 hr' },
];

const EditBookingModal = ({
  isOpen,
  onClose,
  trip,
  assets = EMPTY_ARRAY,
  teamMembers = EMPTY_ARRAY,
  roles = EMPTY_ARRAY,
  allBookings = EMPTY_ARRAY,
  onSave,
  onGeneratePaymentLink,
  timeFormat,
  services = EMPTY_ARRAY,
  addOns = EMPTY_ARRAY
}) => {
  // Locale-aware money formatter — booking amounts are stored in the org's base
  // currency, which matches the operator locale (USD for US, AED for UAE). Never
  // hard-code "$" in this modal.
  const { formatMoney, currencySymbol, taxLabel, isUAE, dateFormat } = useOperatorLocale();
  const minCharge = useMinChargeFloor();
  const money = (amount) => formatMoney(Number(amount) || 0);

  // Locale-aware date helpers (avoid TZ drift by anchoring to local noon).
  const isoToDate = (iso) => (iso ? new Date(`${iso}T12:00:00`) : undefined);
  const dateToIso = (dt) => {
    if (!dt) return '';
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const formatDateDisplay = (iso) => {
    if (!iso) return 'Pick a date';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return (dateFormat || 'MM/DD/YYYY').replace('YYYY', y).replace('MM', m).replace('DD', d);
  };
  const [dateOpen, setDateOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  // Booking/pricing model — immutable, stamped at creation. Drives the schedule UI.
  const durationModel = trip?.durationModel || trip?.duration_model
    || ((trip?.checkOutDate || trip?.check_out_date) ? 'flexible_dates' : 'slot_based');

  const [formData, setFormData] = useState({
    // Schedule
    date: '',
    startTime: '',
    duration: 60,
    timezone: '',
    // Customer
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    partySize: 1,
    emergencyContactNumber: '',
    notes: '',
    // Assignment
    assetId: '',
    teamAssignments: [],
    // Location
    city: '',
    country: '',
    pickupLocation: '',
    // Status
    paymentStatus: 'unpaid',
    checkedIn: false,
    // Add-ons
    selectedAddOns: [],
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [serviceAddOns, setServiceAddOns] = useState([]);
  const [priceBreakdown, setPriceBreakdown] = useState({
    original: 0,
    current: 0,
    difference: 0
  });
  const [saving, setSaving] = useState(false);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const [activeTab, setActiveTab] = useState('trip');

  // Notify step state — shown after save to confirm who gets notified
  const [showNotifyStep, setShowNotifyStep] = useState(false);
  const [notifyTeam, setNotifyTeam] = useState(true);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [pendingSaveData, setPendingSaveData] = useState(null); // holds save payload until notify confirmed

  // Cancel / Delete dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotifyEmail, setCancelNotifyEmail] = useState(true);
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  
  // Add Extras state
  const [newExtras, setNewExtras] = useState([]); // Add-ons selected to add
  const [extrasPaymentMethod, setExtrasPaymentMethod] = useState('');
  const [extrasCurrency, setExtrasCurrency] = useState('');
  const [processingExtras, setProcessingExtras] = useState(false);
  const [customExtraAmount, setCustomExtraAmount] = useState(''); // For custom payment amount
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  
  const originalPriceRef = useRef(0);

  // Load supported currencies for multi-currency checkout
  useEffect(() => {
    const loadSupportedCurrencies = async () => {
      try {
        const orgCountry = localStorage.getItem('orgCountry') || 'US';
        const response = await api.get(`/admin/supported-currencies?org_country=${orgCountry}`);
        if (response.data?.currencies) {
          const sorted = [...response.data.currencies].sort((a, b) => {
            if (a.tier === 'guaranteed' && b.tier !== 'guaranteed') return -1;
            if (a.tier !== 'guaranteed' && b.tier === 'guaranteed') return 1;
            return 0;
          });
          setSupportedCurrencies(sorted);
        }
      } catch (error) {
        console.error('Failed to load supported currencies:', error);
      }
    };
    loadSupportedCurrencies();
  }, []);

  // Initialize form data when trip changes
  useEffect(() => {
    if (trip) {
      // Check if the assigned asset has team requirements
      let initialTeamAssignments = trip.teamAssignments || trip.team_assignments || [];
      
      // If asset is assigned but no team assignments, populate from asset requirements
      const tripAssetId = trip.assetId || trip.asset_id;
      if (tripAssetId && initialTeamAssignments.length === 0 && assets.length > 0) {
        const selectedAsset = assets.find(b => b.id === tripAssetId);
        const autoTeamSlots = buildTeamSlotsFromAsset(selectedAsset, roles);
        if (autoTeamSlots.length > 0) initialTeamAssignments = autoTeamSlots;
      }
      
      const initialData = {
        // Handle both snake_case (from Schedule API) and camelCase (from Bookings API)
        date: trip.date || trip.bookingDate || trip.booking_date || '',
        startTime: trip.startTime || trip.start_time || '',
        duration: trip.duration || trip.durationMinutes || trip.duration_minutes || 60,
        checkOutDate: trip.checkOutDate || trip.check_out_date || '',
        numNights: trip.numNights || trip.num_nights || null,
        timezone: trip.timezone || localStorage.getItem('orgTimezone') || 'America/Chicago',
        customerName: trip.customerName || trip.customer_name || '',
        customerPhone: trip.customerPhone || trip.customer_phone || '',
        customerEmail: trip.customerEmail || trip.customer_email || '',
        partySize: trip.partySize || trip.party_size || 1,
        emergencyContactNumber: trip.emergencyContactNumber || trip.emergency_contact_number || '',
        notes: trip.notes || trip.internalNotes || trip.internal_notes || '',
        assetId: trip.assetId || trip.asset_id || '',
        teamAssignments: initialTeamAssignments,
        city: trip.city || '',
        country: trip.country || '',
        pickupLocation: trip.pickupLocation || trip.meeting_point || trip.pickup_location || trip.locationDetails || '',
        paymentStatus: trip.paymentStatus || trip.payment_status || 'unpaid',
        checkedIn: trip.checkedIn || trip.checked_in || false,
        selectedAddOns: trip.selectedAddOns || trip.selected_add_ons || trip.add_ons || [],
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setActiveTab('trip');
      
      const originalTotal = trip.paidAmount || trip.paid_amount || trip.totalAmount || trip.total_price || trip.servicePrice || 0;
      originalPriceRef.current = originalTotal;
      setPriceBreakdown({
        original: originalTotal,
        current: originalTotal,
        difference: 0
      });
      
      const tripServiceId = trip.serviceId || trip.service_id;
      if (tripServiceId && services.length > 0) {
        const service = services.find(s => s.id === tripServiceId);
        if (service?.addOns) {
          setServiceAddOns(service.addOns);
        }
      }
    }
  }, [trip, services, assets, roles]);

  // Calculate price when add-ons change
  useEffect(() => {
    if (!originalData) return;
    
    const originalAddOnsTotal = (originalData.selectedAddOns || []).reduce(
      (sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0
    );
    
    const currentAddOnsTotal = (formData.selectedAddOns || []).reduce(
      (sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0
    );
    
    const basePrice = (originalPriceRef.current || 0) - originalAddOnsTotal;
    const newTotal = basePrice + currentAddOnsTotal;
    const difference = currentAddOnsTotal - originalAddOnsTotal;
    
    setPriceBreakdown({
      original: originalPriceRef.current,
      current: newTotal,
      difference: difference
    });
  }, [formData.selectedAddOns, originalData]);

  // Helper functions
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const checkTimeOverlap = (trip1Start, trip1Duration, trip2Start, trip2Duration) => {
    const start1 = timeToMinutes(trip1Start);
    const end1 = start1 + trip1Duration;
    const start2 = timeToMinutes(trip2Start);
    const end2 = start2 + trip2Duration;
    return start1 < end2 && end1 > start2;
  };

  const calculateEndTime = (startTime, duration) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + duration;
    const hours = Math.floor(endMinutes / 60) % 24;
    const mins = endMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Check for conflicts
  useEffect(() => {
    if (!formData.startTime || !formData.date) return;

    const detectedConflicts = [];
    const availableSuggestions = [];

    if (formData.assetId && formData.assetId !== 'unassigned') {
      const assetConflicts = allBookings.filter(t =>
        t.id !== trip?.id &&
        t.date === formData.date &&
        t.assetId === formData.assetId &&
        checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
      );

      if (assetConflicts.length > 0) {
        const asset = assets.find(b => b.id === formData.assetId);
        detectedConflicts.push({
          type: 'asset',
          message: `${asset?.name} is booked ${assetConflicts[0].startTime} - ${calculateEndTime(assetConflicts[0].startTime, assetConflicts[0].duration)}`
        });

        const availableAssets = assets.filter(b =>
          b.active &&
          !allBookings.some(t =>
            t.id !== trip?.id &&
            t.date === formData.date &&
            t.assetId === b.id &&
            checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
          )
        );

        if (availableAssets.length > 0) {
          availableSuggestions.push({
            type: 'asset',
            items: availableAssets.slice(0, 3).map(v => ({ id: v.id, name: v.name }))
          });
        }
      }
    }

    // Capacity sanity: warn when the chosen boat can't fit the party
    if (formData.assetId && formData.assetId !== 'unassigned') {
      const selAsset = assets.find(b => b.id === formData.assetId);
      if (selAsset?.capacity && Number(formData.partySize) > Number(selAsset.capacity)) {
        detectedConflicts.push({
          type: 'capacity',
          message: `${selAsset.name} fits ${selAsset.capacity} guests — this party is ${formData.partySize}. Assign a bigger boat.`
        });
      }
    }

    formData.teamAssignments.forEach((assignment) => {
      if (assignment.teamId) {
        const teamConflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === assignment.teamId) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );

        if (teamConflicts.length > 0) {
          const team = teamMembers.find(c => c.id === assignment.teamId);
          detectedConflicts.push({
            type: 'team',
            message: `${team?.name} is booked ${teamConflicts[0].startTime} - ${calculateEndTime(teamConflicts[0].startTime, teamConflicts[0].duration)}`
          });
        }
      }
    });

    setConflicts(detectedConflicts);
    setSuggestions(availableSuggestions);
  }, [formData.startTime, formData.date, formData.assetId, formData.partySize, formData.teamAssignments, formData.duration, allBookings, trip, assets, teamMembers]);

  // Handle asset change - auto-template team slots
  const handleAssetChange = (assetId) => {
    setFormData(prev => ({ ...prev, assetId: assetId }));

    if (assetId !== 'unassigned' && assetId) {
      const selectedAsset = assets.find(b => b.id === assetId);
      if (selectedAsset?.teamRequirements) {
        const autoTeamSlots = buildTeamSlotsFromAsset(selectedAsset, roles);
        setFormData(prev => ({ ...prev, teamAssignments: autoTeamSlots }));
      }
    }
  };

  // Team assignment handler
  const handleTeamChange = (idx, value) => {
    const newAssignments = [...formData.teamAssignments];
    const selectedTeam = teamMembers.find(t => t.id === value);
    newAssignments[idx] = {
      ...newAssignments[idx],
      teamId: value === 'unassigned' ? '' : value,
      teamName: selectedTeam?.name || ''
    };
    setFormData(prev => ({ ...prev, teamAssignments: newAssignments }));
  };

  // Add-on handlers
  const handleAddOnToggle = (addon, checked) => {
    setFormData(prev => {
      if (checked) {
        return {
          ...prev,
          selectedAddOns: [...prev.selectedAddOns, { ...addon, quantity: 1 }]
        };
      } else {
        return {
          ...prev,
          selectedAddOns: prev.selectedAddOns.filter(a => a.id !== addon.id && a.name !== addon.name)
        };
      }
    });
  };

  const handleAddOnQuantityChange = (addonId, quantity) => {
    setFormData(prev => ({
      ...prev,
      selectedAddOns: prev.selectedAddOns.map(addon =>
        (addon.id === addonId || addon.name === addonId) 
          ? { ...addon, quantity: Math.max(1, quantity) }
          : addon
      )
    }));
  };

  const isAddOnSelected = (addon) => {
    return formData.selectedAddOns.some(a => a.id === addon.id || a.name === addon.name);
  };

  const getAddOnQuantity = (addon) => {
    const selected = formData.selectedAddOns.find(a => a.id === addon.id || a.name === addon.name);
    return selected?.quantity || 1;
  };

  // Cancel booking handler
  const handleCancelBooking = async () => {
    const orgId = localStorage.getItem('organizationId');
    if (!orgId || !trip?.id) return;
    setCancelProcessing(true);
    try {
      await apiV2.post(`/organizations/${orgId}/bookings/${trip.id}/cancel`, {
        reason: cancelReason || null,
        notify_customer: cancelNotifyEmail,
      });
      toast.success('Booking cancelled');
      invalidateFinancials();
      setShowCancelDialog(false);
      onClose();
      if (window.refreshBookingData) window.refreshBookingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelProcessing(false);
    }
  };

  // Hard delete handler
  const handleDeleteBooking = async () => {
    const orgId = localStorage.getItem('organizationId');
    if (!orgId || !trip?.id) return;
    setDeleteProcessing(true);
    try {
      await apiV2.delete(`/organizations/${orgId}/bookings/${trip.id}`);
      toast.success('Booking record permanently deleted');
      invalidateFinancials();
      setShowDeleteDialog(false);
      onClose();
      if (window.refreshBookingData) window.refreshBookingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete booking');
    } finally {
      setDeleteProcessing(false);
    }
  };

  // Save handler
  const handleSave = async () => {
    if (conflicts.length > 0) {
      const confirmSave = window.confirm(
        `Warning: ${conflicts.length} conflict(s) detected:\n${conflicts.map(c => c.message).join('\n')}\n\nDo you want to save anyway?`
      );
      if (!confirmSave) return;
    }

    setSaving(true);
    
    try {
      const priceDiff = priceBreakdown.difference;
      
      if (Math.abs(priceDiff) > 0.01) {
        if (priceDiff > 0) {
          try {
            // Charge the add-on difference through the canonical extras-checkout
            // endpoint (SSOT 3% gross platform fee + full Stripe metadata).
            // Single synthetic line item priced at the exact difference keeps the
            // Stripe charge penny-accurate (fee/tax computed on this delta only).
            const chargeResponse = await api.post('/admin/bookings/create-extras-checkout', {
              bookingId: trip.id,
              confirmationCode: trip.confirmationCode || trip.confirmation_code,
              amount: priceDiff,
              currency: (trip.currency || 'USD').toUpperCase(),
              addOns: [{ name: 'Additional add-ons', price: priceDiff, quantity: 1 }],
              paymentMethod: 'pay_now_redirect',
              customerEmail: trip.customerEmail || trip.customer_email,
              customerName: trip.customerName || trip.customer_name
            });
            
            if (chargeResponse.data.checkoutUrl) {
              toast.info('Redirecting to payment for additional charges...');
              window.location.href = chargeResponse.data.checkoutUrl;
              return;
            } else {
              toast.success(`Additional charge of ${money(priceDiff)} processed`);
              invalidateFinancials();
            }
          } catch (chargeError) {
            console.error('Charge error:', chargeError);
            toast.error('Could not process additional charge. Saving booking without payment update.');
          }
        } else if (priceDiff < 0) {
          toast.info(
            `Price decreased by ${money(Math.abs(priceDiff))}. If you need to refund this amount, do so in your Stripe Dashboard.`,
            { duration: 8000 }
          );
        }
      }
      
      // Build save payload
      const savePayload = {
        ...formData,
        totalAmount: priceBreakdown.current,
        priceModified: Math.abs(priceDiff) > 0.01,
      };

      // Determine who can be notified
      const assignedMembers = (formData.teamAssignments || [])
        .filter(a => a.teamId && a.teamId !== 'unassigned')
        .map(a => teamMembers.find(m => m.id === a.teamId))
        .filter(Boolean);
      
      // Check for un-assigned members (were assigned before, now removed)
      const originalAssignedIds = new Set(
        (originalData?.teamAssignments || [])
          .filter(a => a.teamId && a.teamId !== 'unassigned')
          .map(a => a.teamId)
      );
      const currentAssignedIds = new Set(
        (formData.teamAssignments || [])
          .filter(a => a.teamId && a.teamId !== 'unassigned')
          .map(a => a.teamId)
      );
      const hasUnassignments = [...originalAssignedIds].some(id => !currentAssignedIds.has(id));
      const hasNewAssignments = [...currentAssignedIds].some(id => !originalAssignedIds.has(id));

      // Detect if anything actually changed from original
      const hasChanges = (() => {
        if (!originalData) return true; // no baseline, assume changed
        const fields = ['date', 'startTime', 'duration', 'checkOutDate', 'customerName', 'customerPhone',
          'customerEmail', 'partySize', 'emergencyContactNumber', 'notes', 'assetId',
          'city', 'country', 'pickupLocation', 'paymentStatus', 'checkedIn'];
        for (const f of fields) {
          if (String(formData[f] || '') !== String(originalData[f] || '')) return true;
        }
        // Check add-ons
        const currAO = (formData.selectedAddOns || []).map(a => a.name || '').sort().join(',');
        const origAO = (originalData.selectedAddOns || []).map(a => a.name || '').sort().join(',');
        if (currAO !== origAO) return true;
        // Check team assignments
        if (hasUnassignments || hasNewAssignments) return true;
        return false;
      })();

      if (!hasChanges) {
        // Nothing changed — just close without saving or notifying
        onClose();
        toast('No changes to save');
        setSaving(false);
        return;
      }

      const hasCustomer = !!(formData.customerName && (formData.customerPhone || formData.customerEmail || trip?.customerEmail));
      const canNotifySomeone = assignedMembers.length > 0 || hasUnassignments || hasCustomer;

      if (canNotifySomeone) {
        // Show notify step before committing
        setPendingSaveData(savePayload);
        setNotifyTeam(assignedMembers.length > 0 || hasUnassignments);
        setNotifyCustomer(hasCustomer);
        setShowNotifyStep(true);
        setSaving(false);
        return;
      }

      // No one to notify — save directly
      await onSave(trip.id, { ...savePayload, notifyTeam: false, notifyCustomer: false });
      invalidateFinancials();
      onClose();
      toast.success('Booking saved');
    } catch (error) {
      console.error('❌ [EditBookingModal] Save error:', error.response?.data || error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : typeof detail === 'object' ? detail?.message : null;
      toast.error(msg || 'Failed to save booking. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  // Called from the notify step — actually commits the save with notification flags
  const handleConfirmSave = async (notify_team, notify_customer) => {
    if (!pendingSaveData) return;
    setSaving(true);
    try {
      await onSave(trip.id, { ...pendingSaveData, notifyTeam: notify_team, notifyCustomer: notify_customer });
      invalidateFinancials();

      // Build toast message
      const parts = [];
      if (notify_team) {
        const names = (formData.teamAssignments || [])
          .filter(a => a.teamId && a.teamId !== 'unassigned')
          .map(a => { const m = teamMembers.find(t => t.id === a.teamId); return m?.name?.split(' ')[0]; })
          .filter(Boolean);
        // Also count removed members
        const origIds = new Set((originalData?.teamAssignments || []).filter(a => a.teamId && a.teamId !== 'unassigned').map(a => a.teamId));
        const currIds = new Set((formData.teamAssignments || []).filter(a => a.teamId && a.teamId !== 'unassigned').map(a => a.teamId));
        const removedCount = [...origIds].filter(id => !currIds.has(id)).length;
        if (names.length) parts.push(names.join(' & '));
        if (removedCount > 0) parts.push(`${removedCount} removed`);
      }
      if (notify_customer && formData.customerName) parts.push('customer');
      const toastMsg = parts.length
        ? `Saved · ${parts.join(' + ')} notified`
        : 'Booking saved';

      setShowNotifyStep(false);
      setPendingSaveData(null);
      onClose();
      toast.success(toastMsg);
    } catch (error) {
      console.error('❌ Save failed:', error.response?.data || error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 
                  typeof detail === 'object' ? detail?.message : null;
      toast.error(msg || 'Failed to save booking. Please try again.');
      setSaving(false);
    }
  };

  // Get assets with availability status
  const assetsWithStatus = useMemo(() => {
    return assets.map(asset => {
      let available = true;
      let reason = '';

      if (!asset.active) {
        available = false;
        reason = 'Inactive';
      }

      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.assetId === asset.id &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Booked ${conflicts[0].startTime}`;
        }
      }

      return { ...asset, available, reason };
    });
  }, [assets, formData.startTime, formData.date, formData.duration, allBookings, trip?.id]);

  // Get team members filtered by roleIds (supports OR logic)
  const getTeamWithStatus = useCallback((roleIds) => {
    return getTeamMembersWithStatus({
      teamMembers,
      roles,
      roleIds,
      date: formData.date,
      startTime: formData.startTime,
      durationMinutes: formData.duration,
      allBookings,
      currentBookingId: trip?.id,
    });
  }, [teamMembers, roles, formData.startTime, formData.date, formData.duration, allBookings, trip?.id]);

  // Guard against invalid trip object
  if (!trip || typeof trip !== 'object') return null;
  
  // Validate trip has required properties (guard against error objects being passed)
  if (trip.error || trip.detail) {
    console.error('EditBookingModal received an error object instead of trip:', trip);
    return null;
  }

  const availableAddOns = serviceAddOns.length > 0 ? serviceAddOns : addOns;
  const selectedAsset = assets.find(a => a.id === formData.assetId);
  const hasExtras = availableAddOns.length > 0 || (trip.customAnswers && Object.keys(trip.customAnswers).length > 0);
  
  // Get fee breakdown - prefer feeBreakdown object for accuracy
  const fb = trip?.feeBreakdown || {};
  
  // Fee breakdown for display - use feeBreakdown object for accuracy
  const breakdown = {
    subtotal: fb.subtotal || trip?.basePrice || trip?.servicePrice || 0,
    addOnsTotal: trip?.addOnsTotal || 0,
    platformFee: fb.platformFee || trip?.platformFee || 0,
    stripeFee: fb.stripeFee || trip?.stripeFee || 0,
    serviceFee: fb.serviceFee || trip?.serviceFee || 0,
    salesTax: fb.salesTax || trip?.salesTax || 0,
    taxRate: fb.taxRate || trip?.taxRate || 0,
    totalFees: fb.totalFees || ((fb.platformFee || 0) + (fb.stripeFee || 0) + (fb.serviceFee || 0)),
  };
  
  // Calculate Total Due = subtotal + fees + tax
  // The stored total_price might be incorrect (missing fees/tax), so we calculate it correctly
  const calculatedTotal = breakdown.subtotal + breakdown.totalFees + breakdown.salesTax;
  
  // Use calculated total if it's larger than stored (means stored is missing fees/tax)
  // Otherwise use stored value (feeBreakdown.grandTotal or totalAmount)
  const storedTotal = fb.grandTotal || trip?.totalAmount || trip?.totalPrice || 0;
  const totalDue = calculatedTotal > storedTotal ? calculatedTotal : storedTotal;
  
  const amountPaid = trip?.paidAmount || trip?.amountPaid || 0;
  const remaining = Math.max(0, totalDue - amountPaid);
  
  // Check if considered "paid" (within 5 cents tolerance)
  const isPaid = remaining <= 0.05;
  
  // Check if this is a past booking
  // NOTE: trip.date is YYYY-MM-DD — append T00:00:00 to parse as local midnight (see dateUtils.js contract)
  const isPastBooking = trip?.date && new Date(trip.date + 'T00:00:00') < new Date(new Date().toDateString());

  const isCancelled = trip?.status === 'cancelled';
  const tripPaymentStatus = trip?.paymentStatus || trip?.payment_status;
  const isRefundable = ['paid', 'partial', 'completed'].includes(tripPaymentStatus) && !isCancelled;

  return (
    <>
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Booking"
      description={
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="font-mono text-xs bg-blue-50 text-blue-700 border-0 hover:bg-blue-50">{typeof trip.confirmationCode === 'string' ? trip.confirmationCode : (trip.confirmationCode?.toString?.() || '')}</Badge>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">{typeof trip.serviceName === 'string' ? trip.serviceName : (trip.serviceName || 'Booking')}</span>
        </div>
      }
      size="md"
      footer={
        <div className="flex items-center gap-2 w-full">
          {/* Danger actions — left side */}
          <div className="flex items-center gap-2 mr-auto">
            {!isCancelled && (
              <Button
                variant="outline"
                size="sm"
                data-testid="cancel-booking-btn"
                onClick={() => setShowCancelDialog(true)}
                className="border-gray-200 text-gray-600 hover:text-amber-700 hover:border-amber-300 hover:bg-amber-50 gap-1.5"
                disabled={saving}
              >
                <XCircle className="w-4 h-4" />
                Cancel
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              data-testid="delete-booking-btn"
              onClick={() => setShowDeleteDialog(true)}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 gap-1.5"
              disabled={saving}
              title="Permanently delete booking"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
          {/* Save — primary action */}
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </PrimaryButton>
        </div>
      }
    >
      {/* Conflicts Warning */}
      {conflicts.length > 0 && (
        <div className="p-3 mb-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 mb-1">Scheduling Conflict</p>
              {conflicts.map((conflict, idx) => (
                <p key={idx} className="text-xs text-orange-700">• {conflict.message}</p>
              ))}
            </div>
          </div>
          {suggestions.length > 0 && suggestions[0].type === 'asset' && (
            <div className="mt-2 pt-2 border-t border-orange-200">
              <p className="text-xs text-orange-800 mb-1">Available alternatives:</p>
              <div className="flex flex-wrap gap-1">
                {suggestions[0].items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => handleAssetChange(item.id)}
                    className="px-2 py-1 bg-white border border-orange-300 rounded text-xs text-orange-700 hover:bg-orange-100"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-5 gap-1 mb-4 p-1 h-auto">
          <TabsTrigger value="trip" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Trip</TabsTrigger>
          <TabsTrigger value="customer" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Guest</TabsTrigger>
          <TabsTrigger value="assignment" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Assign</TabsTrigger>
          <TabsTrigger value="payment" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Pay</TabsTrigger>
          <TabsTrigger value="addons" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Extras</TabsTrigger>
        </TabsList>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 1: TRIP - Schedule, Location, Guests
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="trip" className="space-y-5 mt-0">
          {/* Schedule Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="w-4 h-4" />
              Schedule
              {durationModel === 'day_pass' && (
                <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  <Sun className="w-3 h-3" /> Day Pass
                </span>
              )}
              {durationModel === 'flexible_dates' && (
                <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                  <CalendarDays className="w-3 h-3" /> Date Range
                </span>
              )}
            </Label>

            {durationModel === 'day_pass' ? (
              /* ── Day pass: date only, whole-day access ── */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Date</Label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" data-testid="edit-booking-date-trigger"
                        className={`h-11 w-full justify-start text-left font-normal ${!formData.date ? 'text-gray-400' : 'text-gray-900'}`}>
                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                        {formatDateDisplay(formData.date)}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker mode="single" selected={isoToDate(formData.date)}
                        onSelect={(dt) => { if (dt) setFormData(prev => ({ ...prev, date: dateToIso(dt) })); setDateOpen(false); }}
                        initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Access</Label>
                  <div className="h-11 flex items-center gap-2 px-3 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-700 font-medium">
                    <Sun className="w-4 h-4" /> All-day access
                  </div>
                </div>
              </div>
            ) : durationModel === 'flexible_dates' ? (
              /* ── Date range: check-in date + time, check-out date, nights ── */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Check-in</Label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" data-testid="edit-booking-date-trigger"
                        className={`h-11 w-full justify-start text-left font-normal ${!formData.date ? 'text-gray-400' : 'text-gray-900'}`}>
                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                        {formatDateDisplay(formData.date)}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker mode="single" selected={isoToDate(formData.date)}
                        onSelect={(dt) => {
                          if (dt) setFormData(prev => {
                            const newDate = dateToIso(dt);
                            const nights = prev.checkOutDate ? Math.max(0, Math.round((isoToDate(prev.checkOutDate) - isoToDate(newDate)) / 86400000)) : prev.numNights;
                            return { ...prev, date: newDate, numNights: nights };
                          });
                          setDateOpen(false);
                        }} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Check-out</Label>
                  <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" data-testid="edit-booking-checkout-trigger"
                        className={`h-11 w-full justify-start text-left font-normal ${!formData.checkOutDate ? 'text-gray-400' : 'text-gray-900'}`}>
                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                        {formData.checkOutDate ? formatDateDisplay(formData.checkOutDate) : 'Set check-out'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker mode="single" selected={isoToDate(formData.checkOutDate)}
                        disabled={(d) => formData.date && d < isoToDate(formData.date)}
                        onSelect={(dt) => {
                          if (dt) setFormData(prev => {
                            const co = dateToIso(dt);
                            const nights = prev.date ? Math.max(0, Math.round((isoToDate(co) - isoToDate(prev.date)) / 86400000)) : prev.numNights;
                            return { ...prev, checkOutDate: co, numNights: nights };
                          });
                          setCheckOutOpen(false);
                        }} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Check-in time</Label>
                  <TimeSlotSelector value={formData.startTime}
                    onChange={(time) => setFormData(prev => ({ ...prev, startTime: time }))} className="w-full" />
                </div>
                {formData.numNights != null && (
                  <div className="sm:col-span-3 text-xs text-gray-500">
                    {formData.numNights} night{formData.numNights !== 1 ? 's' : ''} stay
                  </div>
                )}
              </div>
            ) : (
              /* ── Slot-based (default): date + time + duration ── */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Date</Label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="edit-booking-date-trigger"
                        className={`h-11 w-full justify-start text-left font-normal ${!formData.date ? 'text-gray-400' : 'text-gray-900'}`}
                      >
                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                        {formatDateDisplay(formData.date)}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker
                        mode="single"
                        selected={isoToDate(formData.date)}
                        onSelect={(dt) => {
                          if (dt) setFormData(prev => ({ ...prev, date: dateToIso(dt) }));
                          setDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Time</Label>
                  <TimeSlotSelector
                    value={formData.startTime}
                    onChange={(time) => setFormData(prev => ({ ...prev, startTime: time }))}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col">
                  <Label className="text-xs text-gray-500 mb-1 block">Duration</Label>
                  <Select
                    value={String(formData.duration)}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, duration: parseInt(val) }))}
                  >
                    <SelectTrigger className="h-11 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Timezone</Label>
              <Select
                value={formData.timezone}
                onValueChange={(val) => setFormData(prev => ({ ...prev, timezone: val }))}
              >
                <SelectTrigger className="h-11 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Guests */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4" />
              Guests
            </Label>
            <Input
              type="number"
              min={1}
              value={formData.partySize}
              onChange={(e) => setFormData(prev => ({ ...prev, partySize: parseInt(e.target.value) || 1 }))}
              className="h-11 w-24"
            />
          </div>

          {/* Location */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MapPin className="w-4 h-4" />
              Location
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                  className="h-11"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Country"
                  className="h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Meeting Point</Label>
              <Input
                value={formData.pickupLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, pickupLocation: e.target.value }))}
                placeholder="Where to meet the customer"
                className="h-11"
              />
            </div>
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 2: CUSTOMER - Contact info, Emergency, Notes
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="customer" className="space-y-5 mt-0">
          {/* Customer Details */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <User className="w-4 h-4" />
              Customer Details
            </Label>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.customerName}
                onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Full name"
                className="h-11"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Email
                </Label>
                <Input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                  placeholder="email@example.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Phone
                </Label>
                <Input
                  value={formData.customerPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Emergency Contact
            </Label>
            <Input
              value={formData.emergencyContactNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactNumber: e.target.value }))}
              placeholder="Emergency contact phone"
              className="h-11"
            />
          </div>

          {/* Internal Notes */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              Internal Notes
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Notes visible only to your team..."
              rows={3}
              className="resize-none min-h-[80px]"
            />
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 3: ASSIGNMENT - Asset & Team
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="assignment" className="space-y-5 mt-0">
          {/* Asset Selection */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Anchor className="w-4 h-4" />
              Asset
            </Label>
            <Select 
              value={formData.assetId || 'unassigned'} 
              onValueChange={(value) => handleAssetChange(value)}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Select Asset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">— No Asset —</SelectItem>
                {assetsWithStatus.map(asset => (
                  <SelectItem 
                    key={asset.id} 
                    value={asset.id}
                    disabled={!asset.available}
                  >
                    <span className={!asset.available ? 'text-gray-400' : ''}>
                      {asset.name} {!asset.available && `(${asset.reason})`}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedAsset && (
              <p className="text-xs text-gray-500 mt-1">
                Capacity: {selectedAsset.capacity} • {selectedAsset.teamRequirements?.length || 0} team role(s)
              </p>
            )}
          </div>

          {/* Team Assignments */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4" />
              Team
            </Label>
            {formData.teamAssignments && formData.teamAssignments.length > 0 ? (
              <TeamAssignmentSelector
                teamAssignments={formData.teamAssignments}
                roles={roles}
                getTeamWithStatus={getTeamWithStatus}
                onTeamChange={handleTeamChange}
                testIdPrefix="edit-team-assign"
              />
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {formData.assetId ? 'No team roles required' : 'Select an asset first'}
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 4: PAYMENT - Status, Amounts, Waiver, Check-in
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="payment" className="space-y-5 mt-0">
          {/* Payment Status & Check-in Row */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Status Dropdown */}
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-gray-500" />
              <Select
                value={formData.paymentStatus || 'pending'}
                onValueChange={(val) => setFormData(prev => ({ ...prev, paymentStatus: val }))}
              >
                <SelectTrigger className={`w-32 h-9 text-xs font-medium border ${
                  formData.paymentStatus === 'paid' ? 'bg-green-50 border-green-200 text-green-700' :
                  formData.paymentStatus === 'deposit_paid' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                  formData.paymentStatus === 'partial' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                  formData.paymentStatus === 'refunded' ? 'bg-gray-50 border-gray-200 text-gray-600' :
                  formData.paymentStatus === 'partially_refunded' ? 'bg-orange-50 border-orange-200 text-orange-700' :
                  'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${
                      formData.paymentStatus === 'paid' ? 'bg-green-500' :
                      formData.paymentStatus === 'deposit_paid' ? 'bg-blue-500' :
                      formData.paymentStatus === 'partial' ? 'bg-yellow-500' :
                      formData.paymentStatus === 'refunded' ? 'bg-gray-400' :
                      formData.paymentStatus === 'partially_refunded' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`} />
                    <span>
                      {formData.paymentStatus === 'paid' ? 'Paid' :
                       formData.paymentStatus === 'deposit_paid' ? 'Deposit' :
                       formData.paymentStatus === 'partial' ? 'Partial' :
                       formData.paymentStatus === 'refunded' ? 'Refunded' :
                       formData.paymentStatus === 'partially_refunded' ? 'Part Refund' :
                       'Unpaid'}
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="pending">Unpaid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="partially_refunded">Part Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Check-in Toggle */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, checkedIn: !prev.checkedIn }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                formData.checkedIn 
                  ? 'bg-green-100 text-green-700 border border-green-200' 
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Check className={`w-3.5 h-3.5 ${formData.checkedIn ? 'text-green-600' : 'text-gray-400'}`} />
              {formData.checkedIn ? 'Checked In' : 'Not Checked In'}
            </button>
          </div>

          {/* Past Booking Warning */}
          {isPastBooking && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700">This booking is in the past. Changes may affect records.</p>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Price Breakdown</p>
            
            {/* Line items - show all fees for transparency */}
            <div className="space-y-1.5 text-sm">
              {/* Subtotal (base + add-ons) */}
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">{money(breakdown.subtotal)}</span>
              </div>
              
              {/* Add-ons — itemized name + price when available */}
              {(formData.selectedAddOns || []).length > 0 ? (
                (formData.selectedAddOns || []).map((addon, idx) => (
                  <div key={`bd-addon-${addon.id || idx}`} className="flex justify-between text-xs pl-3">
                    <span className="text-gray-500">└ {addon.name || 'Add-on'}{(addon.quantity || 1) > 1 ? ` ×${addon.quantity}` : ''}</span>
                    <span className="text-gray-500">{money((addon.price || 0) * (addon.quantity || 1))}</span>
                  </div>
                ))
              ) : breakdown.addOnsTotal > 0 && (
                <div className="flex justify-between text-xs pl-3">
                  <span className="text-gray-500">└ Includes add-ons</span>
                  <span className="text-gray-500">{money(breakdown.addOnsTotal)}</span>
                </div>
              )}
              
              {/* Fees Section */}
              {(breakdown.platformFee > 0 || breakdown.stripeFee > 0 || breakdown.serviceFee > 0) && (
                <>
                  <div className="flex justify-between pt-1">
                    <span className="text-gray-600">Fees</span>
                    <span className="text-gray-900">{money(breakdown.totalFees)}</span>
                  </div>
                  {breakdown.platformFee > 0 && (
                    <div className="flex justify-between text-xs pl-3">
                      <span className="text-gray-500">└ Platform fee</span>
                      <span className="text-gray-500">{money(breakdown.platformFee)}</span>
                    </div>
                  )}
                  {breakdown.stripeFee > 0 && (
                    <div className="flex justify-between text-xs pl-3">
                      <span className="text-gray-500">└ Processing fee</span>
                      <span className="text-gray-500">{money(breakdown.stripeFee)}</span>
                    </div>
                  )}
                  {breakdown.serviceFee > 0 && (
                    <div className="flex justify-between text-xs pl-3">
                      <span className="text-gray-500">└ Service fee</span>
                      <span className="text-gray-500">{money(breakdown.serviceFee)}</span>
                    </div>
                  )}
                </>
              )}
              
              {/* Tax (if any) */}
              {breakdown.salesTax > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{taxLabel} {breakdown.taxRate > 0 && `(${breakdown.taxRate}%)`}</span>
                  <span className="text-gray-900">{money(breakdown.salesTax)}</span>
                </div>
              )}
            </div>
            
            {/* Totals */}
            <div className="pt-3 border-t border-gray-200 space-y-2">
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-gray-700">Total Due</span>
                <span className="text-gray-900">{money(totalDue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount Paid</span>
                <span className={amountPaid > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}>
                  {money(amountPaid)}
                </span>
              </div>
              {remaining > 0.05 && (
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-gray-700">Remaining</span>
                  <span className="text-gray-900">{money(remaining)}</span>
                </div>
              )}
              {remaining <= 0.05 && amountPaid > 0 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Fully Paid</span>
                </div>
              )}
              
              {/* Deposit Info */}
              {(trip?.depositPaid > 0 || trip?.remainingBalance > 0) && (
                <div className="pt-2 border-t border-blue-100 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-600">Deposit Paid</span>
                    <span className="text-blue-600 font-medium">{money(trip.depositPaid || 0)}</span>
                  </div>
                  {Number(trip.remainingBalance || 0) > 0 && (
                    <div className="flex justify-between text-sm font-semibold">
                      <span className="text-gray-700">Balance Due</span>
                      <span className="text-gray-900">{money(trip.remainingBalance)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Collect Payment Button */}
          {remaining > 0.05 && onGeneratePaymentLink && (
            <Button
              variant="outline"
              size="sm"
              className="w-full h-10 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              onClick={() => {
                setGeneratingPaymentLink(true);
                onGeneratePaymentLink(trip).finally(() => setGeneratingPaymentLink(false));
              }}
              disabled={generatingPaymentLink}
            >
              {generatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Payment Link ({money(remaining)})
            </Button>
          )}
          
          {/* Collect Balance Button (for deposit-paid bookings) */}
          {Number(trip?.remainingBalance || 0) > 0.05 && formData.paymentStatus === 'deposit_paid' && (
            <Button
              variant="outline"
              size="sm"
              data-testid="collect-balance-btn"
              className="w-full h-10 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              onClick={async () => {
                setGeneratingPaymentLink(true);
                try {
                  const orgApi = (await import('../utils/apiV2')).default;
                  const res = await orgApi.collectBalance(trip.id);
                  if (res?.checkoutUrl) {
                    window.open(res.checkoutUrl, '_blank');
                    toast.success('Balance payment link opened');
                  }
                } catch (e) {
                  toast.error('Failed to generate balance payment link');
                } finally {
                  setGeneratingPaymentLink(false);
                }
              }}
              disabled={generatingPaymentLink}
            >
              {generatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Collect Remaining Balance ({money(trip.remainingBalance)})
            </Button>
          )}

          {/* Waiver Status */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Waiver</span>
            </div>
            <Badge variant={trip.waiverSigned ? "default" : "secondary"} className="text-xs">
              {trip.waiverSigned ? '✓ Signed' : 'Pending'}
            </Badge>
          </div>

          {/* Reconcile Payment — charge the saved card (post-trip) */}
          <ReconcilePaymentSection orgId={localStorage.getItem('organizationId')} trip={trip} />
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 5: ADD-ONS - Manage add-ons & collect additional payment
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="addons" className="space-y-5 mt-0">
          {/* Current Add-ons */}
          {formData.selectedAddOns.length > 0 && (
            <div>
              <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <Gift className="w-4 h-4" />
                Current Add-ons
              </Label>
              <div className="space-y-2">
                {formData.selectedAddOns.map((addon, idx) => (
                  <div 
                    key={`current-addon-${addon.id || idx}`} 
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200"
                  >
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{addon.name}</p>
                      <p className="text-xs text-gray-500">{money(addon.price || 0)} × {addon.quantity || 1}</p>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {money((addon.price || 0) * (addon.quantity || 1))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Extras Section */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Plus className="w-4 h-4" />
              Add Extras & Collect Payment
            </Label>
            
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              {/* Available Add-ons from Service */}
              {availableAddOns.filter(addon => !formData.selectedAddOns.some(s => s.id === addon.id || s.name === addon.name)).length > 0 && (
                <>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Select add-ons to add:</p>
                    <div className="grid gap-2">
                      {availableAddOns
                        .filter(addon => !formData.selectedAddOns.some(s => s.id === addon.id || s.name === addon.name))
                        .map((addon) => {
                          const isSelected = newExtras.some(e => e.id === addon.id);
                          return (
                            <label
                              key={addon.id || addon.name}
                              className={`
                                flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all
                                ${isSelected 
                                  ? 'bg-blue-50 border border-blue-500 ring-1 ring-blue-500' 
                                  : 'bg-white border border-gray-200 hover:border-blue-200'}
                              `}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewExtras(prev => [...prev, { ...addon, quantity: 1 }]);
                                    } else {
                                      setNewExtras(prev => prev.filter(ex => ex.id !== addon.id));
                                    }
                                  }}
                                  className="w-4 h-4 rounded text-blue-600"
                                />
                                <div>
                                  <p className="font-medium text-gray-900">{addon.name || addon.title}</p>
                                  {addon.description && (
                                    <p className="text-xs text-gray-500 line-clamp-1">{addon.description}</p>
                                  )}
                                </div>
                              </div>
                              <span className="font-semibold text-gray-900">
                                +{money(addon.price || 0)}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-gray-50 text-gray-500">or enter custom amount</span>
                    </div>
                  </div>
                </>
              )}
              
              {/* Custom Amount Input */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">
                  Custom amount to collect:
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{currencySymbol}</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={customExtraAmount}
                    onChange={(e) => setCustomExtraAmount(e.target.value)}
                    className={`${currencySymbol.length > 1 ? 'pl-14' : 'pl-7'} h-11`}
                    data-testid="extras-custom-amount-input"
                  />
                </div>
                {minCharge.isBelow(customExtraAmount) && (
                  <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                    Charges must be at least {minCharge.formatted}.
                  </p>
                )}
              </div>

              {/* Show Payment Options when there's an amount */}
              {(newExtras.length > 0 || parseFloat(customExtraAmount) > 0) && (
                <>
                  {/* Total */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <span className="font-medium text-gray-700">Additional Amount</span>
                    <span className="text-lg font-bold text-blue-600">
                      {money(newExtras.reduce((sum, e) => sum + (e.price || 0) * (e.quantity || 1), 0) + parseFloat(customExtraAmount || 0))}
                    </span>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">How will they pay?</p>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {[
                        { id: 'pay_now_redirect', label: 'Pay Now', icon: CreditCard, color: 'blue' },
                        { id: 'send_link', label: 'Send Link', icon: Send, color: 'purple' },
                        { id: 'paid', label: 'Already Paid', icon: Check, color: 'green' },
                        { id: 'unpaid', label: 'Pay Later', icon: Clock, color: 'yellow' }
                      ].map((method) => {
                        const isSelected = extrasPaymentMethod === method.id;
                        const Icon = method.icon;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => setExtrasPaymentMethod(method.id)}
                            className={`p-3 sm:p-4 border rounded-lg flex items-center gap-2 transition-all min-h-[48px] ${
                              isSelected 
                                ? `border-${method.color}-500 bg-${method.color}-50` 
                                : 'border-gray-200 hover:border-gray-300 bg-white active:bg-gray-50'
                            }`}
                            style={isSelected ? { 
                              borderColor: method.color === 'blue' ? '#3B82F6' : 
                                          method.color === 'purple' ? '#8B5CF6' : 
                                          method.color === 'green' ? '#22C55E' : '#EAB308',
                              backgroundColor: method.color === 'blue' ? '#EFF6FF' : 
                                              method.color === 'purple' ? '#F5F3FF' : 
                                              method.color === 'green' ? '#F0FDF4' : '#FEFCE8'
                            } : {}}
                          >
                            <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-800' : 'text-gray-500'}`} />
                            <span className={`text-xs sm:text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                              {method.label}
                            </span>
                            {isSelected && <Check className="w-4 h-4 ml-auto text-gray-800 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Multi-Currency Dropdown - Only for Stripe methods */}
                  {(extrasPaymentMethod === 'pay_now_redirect' || extrasPaymentMethod === 'send_link') && supportedCurrencies.length > 0 && (
                    <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm">💱</span>
                        <span className="text-sm font-medium text-gray-700">Customer&apos;s Currency</span>
                      </div>
                      <Select
                        value={extrasCurrency || (trip.currency || 'USD').toLowerCase()}
                        onValueChange={setExtrasCurrency}
                      >
                        <SelectTrigger className="h-11 w-full bg-white" data-testid="extras-currency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={(trip.currency || 'USD').toLowerCase()}>
                            {supportedCurrencies.find(c => c.code === (trip.currency || 'USD'))?.flag || '🏳️'} {(trip.currency || 'USD')} - Default (No conversion)
                          </SelectItem>
                          {supportedCurrencies
                            .filter(c => c.code.toLowerCase() !== (trip.currency || 'USD').toLowerCase())
                            .sort((a, b) => a.code.localeCompare(b.code))
                            .map(currency => (
                              <SelectItem key={currency.code} value={currency.code.toLowerCase()}>
                                {currency.flag} {currency.code} - {currency.name}
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Fee Notice for Stripe payments */}
                  {(extrasPaymentMethod === 'pay_now_redirect' || extrasPaymentMethod === 'send_link') && (
                    <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                      Processing fee (5.9% + {currencySymbol}{isUAE ? '1.00' : '0.30'}) + applicable {taxLabel.toLowerCase()} will be added at checkout.
                    </p>
                  )}

                  {/* Process Button */}
                  {extrasPaymentMethod && (() => {
                    const addOnsTotal = newExtras.reduce((sum, e) => sum + (e.price || 0) * (e.quantity || 1), 0);
                    const customAmount = parseFloat(customExtraAmount || 0);
                    const extrasTotal = addOnsTotal + customAmount;
                    // Only enforce the floor on Stripe-touching methods; "paid"/"unpaid"
                    // bookkeeping methods don't talk to Stripe so any amount is fine.
                    const needsStripe = extrasPaymentMethod === 'send_link' || extrasPaymentMethod === 'pay_now_redirect';
                    const belowFloor = needsStripe && extrasTotal > 0 && extrasTotal < minCharge.amount;
                    return (<>
                      {belowFloor && (
                        <p className="text-xs text-amber-600 mb-2 text-center">
                          Stripe charges must be at least {minCharge.formatted}.
                        </p>
                      )}
                      <Button
                        className="w-full h-12 text-base"
                        style={{ background: '#2563EB' }}
                        disabled={processingExtras || belowFloor}
                        data-testid="extras-process-btn"
                        onClick={async () => {
                          setProcessingExtras(true);
                        try {
                          const addOnsTotal = newExtras.reduce((sum, e) => sum + (e.price || 0) * (e.quantity || 1), 0);
                          const customAmount = parseFloat(customExtraAmount || 0);
                          const extrasTotal = addOnsTotal + customAmount;
                          
                          if (extrasPaymentMethod === 'paid') {
                            // Mark as paid - add extras to booking
                            if (newExtras.length > 0) {
                              setFormData(prev => ({
                                ...prev,
                                selectedAddOns: [...prev.selectedAddOns, ...newExtras]
                              }));
                            }
                            toast.success(`${money(extrasTotal)} collected - marked as paid`);
                            // Reset state
                            setNewExtras([]);
                            setExtrasPaymentMethod('');
                            setCustomExtraAmount('');
                          } else if (extrasPaymentMethod === 'unpaid') {
                            // Add to balance due
                            if (newExtras.length > 0) {
                              setFormData(prev => ({
                                ...prev,
                                selectedAddOns: [...prev.selectedAddOns, ...newExtras]
                              }));
                            }
                            toast.success(`${money(extrasTotal)} added to balance due`);
                            setNewExtras([]);
                            setExtrasPaymentMethod('');
                            setCustomExtraAmount('');
                          } else if (extrasPaymentMethod === 'send_link' || extrasPaymentMethod === 'pay_now_redirect') {
                            // Generate Stripe payment link
                            try {
                              const selectedCurrency = extrasCurrency || (trip.currency || 'USD').toLowerCase();
                              
                              // Add extras first
                              if (newExtras.length > 0) {
                                setFormData(prev => ({
                                  ...prev,
                                  selectedAddOns: [...prev.selectedAddOns, ...newExtras]
                                }));
                              }
                              
                              // Call API to create payment link/checkout session
                              const response = await api.post('/admin/bookings/create-extras-checkout', {
                                bookingId: trip.id,
                                confirmationCode: trip.confirmationCode || trip.confirmation_code,
                                amount: extrasTotal,
                                currency: selectedCurrency.toUpperCase(),
                                addOns: newExtras,
                                customAmount: customAmount,
                                paymentMethod: extrasPaymentMethod,
                                customerEmail: trip.customerEmail || trip.customer_email,
                                customerName: trip.customerName || trip.customer_name
                              });
                              
                              if (response.data?.checkoutUrl) {
                                // Show fee breakdown in toast
                                const fees = response.data.feeBreakdown;
                                
                                if (extrasPaymentMethod === 'pay_now_redirect') {
                                  // Redirect to Stripe checkout
                                  window.open(response.data.checkoutUrl, '_blank');
                                  const totalMsg = fees ? ` (Total: ${money(fees.grandTotal)})` : '';
                                  toast.success(`Opening payment page...${totalMsg}`);
                                } else {
                                  // Copy link or show share modal
                                  navigator.clipboard?.writeText(response.data.checkoutUrl);
                                  const totalMsg = fees ? ` Customer will pay: ${money(fees.grandTotal)}` : '';
                                  toast.success(`Payment link copied!${totalMsg}`);
                                }
                              } else if (response.data?.message) {
                                toast.success(response.data.message);
                              }
                              
                              setNewExtras([]);
                              setExtrasPaymentMethod('');
                              setCustomExtraAmount('');
                            } catch (err) {
                              console.error('Error creating payment link:', err);
                              toast.error(err.response?.data?.detail || 'Failed to create payment link');
                            }
                          }
                        } catch (err) {
                          console.error('Error processing extras:', err);
                          toast.error('Failed to process extras');
                        } finally {
                          setProcessingExtras(false);
                        }
                      }}
                    >
                      {processingExtras ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : extrasPaymentMethod === 'send_link' ? (
                        <Send className="w-4 h-4 mr-2" />
                      ) : extrasPaymentMethod === 'pay_now_redirect' ? (
                        <CreditCard className="w-4 h-4 mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {extrasPaymentMethod === 'paid' ? 'Mark as Paid' :
                       extrasPaymentMethod === 'unpaid' ? 'Add to Balance' :
                       extrasPaymentMethod === 'send_link' ? 'Send Payment Link' :
                       extrasPaymentMethod === 'pay_now_redirect' ? 'Pay Now' : 'Confirm'}
                    </Button>
                    </>);
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Custom Responses (Read-only) */}
          {trip.customAnswers && Object.keys(trip.customAnswers).length > 0 && (
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <FileText className="w-4 h-4" />
                Custom Responses
              </Label>
              <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                {Object.entries(trip.customAnswers).map(([question, answer], idx) => (
                  <div key={idx} className="text-sm">
                    <span className="text-gray-500">{question}:</span>{' '}
                    <span className="text-gray-900 font-medium">{String(answer)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </StandardModal>

    {/* ── NOTIFY DIALOG — pops on top of Edit Booking modal after Save ── */}
    <Dialog open={showNotifyStep} onOpenChange={open => { if (!open && !saving) setShowNotifyStep(false); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex flex-col items-center text-center pt-2 pb-1">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <Bell className="w-6 h-6 text-blue-600" />
            </div>
            <DialogTitle className="text-lg">Send notifications?</DialogTitle>
            <DialogDescription className="mt-1">Let people know about this booking update</DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Team members — show when anyone is assigned, reassigned, or unassigned */}
          {(() => {
            const currentAssigned = (formData.teamAssignments || []).filter(a => a.teamId && a.teamId !== 'unassigned');
            const originalAssignedMap = new Map(
              (originalData?.teamAssignments || [])
                .filter(a => a.teamId && a.teamId !== 'unassigned')
                .map(a => [a.teamId, a])
            );
            const currentIds = new Set(currentAssigned.map(a => a.teamId));
            const originalIds = new Set(originalAssignedMap.keys());

            // Who's newly assigned (not in original)
            const newlyAssignedIds = [...currentIds].filter(id => !originalIds.has(id));
            // Who's being removed (was in original, not in current)
            const removedIds = [...originalIds].filter(id => !currentIds.has(id));

            const hasTeamChanges = currentAssigned.length > 0 || removedIds.length > 0;
            if (!hasTeamChanges) return null;

            // Build description lines — names only, no role tags
            const parts = [];
            if (newlyAssignedIds.length > 0) {
              const names = newlyAssignedIds
                .map(id => teamMembers.find(t => t.id === id)?.name)
                .filter(Boolean);
              if (names.length) parts.push(`Assigned: ${names.join(', ')}`);
            }
            if (removedIds.length > 0) {
              const names = removedIds
                .map(id => teamMembers.find(t => t.id === id)?.name)
                .filter(Boolean);
              if (names.length) parts.push(`Removed: ${names.join(', ')}`);
            }
            // If no new/removed but still assigned (e.g. other fields changed), show who's assigned
            if (parts.length === 0 && currentAssigned.length > 0) {
              const names = currentAssigned
                .map(a => teamMembers.find(t => t.id === a.teamId)?.name)
                .filter(Boolean);
              if (names.length) parts.push(names.join(', '));
            }

            return (
              <label className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={notifyTeam}
                  onChange={e => setNotifyTeam(e.target.checked)}
                  className="w-4 h-4 accent-blue-600 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Notify team members</p>
                  <p className="text-xs text-gray-500 mt-0.5">{parts.join(' · ')}</p>
                </div>
                <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </label>
            );
          })()}

          {/* Customer */}
          {(formData.customerName || trip?.customerName) && (
            <label className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={e => setNotifyCustomer(e.target.checked)}
                className="w-4 h-4 accent-blue-600 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Notify customer</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formData.customerName || trip?.customerName}
                  {(formData.customerPhone || trip?.customerPhone) && ` · ${formData.customerPhone || trip?.customerPhone}`}
                </p>
              </div>
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </label>
          )}
        </div>

        <DialogFooter className="gap-2 pt-1">
          <Button
            variant="outline"
            className="flex-1"
            data-testid="edit-save-without-notify"
            onClick={() => handleConfirmSave(false, false)}
            disabled={saving}
          >
            Save without notifying
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="edit-save-and-notify"
            onClick={() => handleConfirmSave(notifyTeam, notifyCustomer)}
            disabled={saving || (!notifyTeam && !notifyCustomer)}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Save & Notify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ── CANCEL BOOKING DIALOG ─────────────────────────────── */}
    <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            Cancel Booking
          </DialogTitle>
          <DialogDescription>
            This will mark the booking as cancelled
            {cancelNotifyEmail ? ' and notify the customer.' : '.'}
          </DialogDescription>
        </DialogHeader>

        {/* Booking summary */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Customer</span>
            <span className="font-medium">{trip?.customerName || trip?.customer_name || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Service</span>
            <span className="font-medium">{trip?.serviceName || trip?.service_name || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Date</span>
            <span className="font-medium">{trip?.date || trip?.bookingDate || '—'}</span>
          </div>
          {(trip?.paidAmount || trip?.amountPaid) > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Paid</span>
              <span className="font-medium text-green-700">{money(trip?.paidAmount || trip?.amountPaid || 0)}</span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Refund note for paid bookings */}
          {isRefundable && (
            <div className="flex items-start gap-3 p-3 rounded-lg border border-blue-100 bg-blue-50">
              <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800">Need to refund this booking?</p>
                <p className="text-blue-600 mt-0.5">
                  Issue the refund directly in your{' '}
                  <button
                    onClick={() => window.open('https://dashboard.stripe.com/payments', '_blank')}
                    className="underline font-medium hover:text-blue-800"
                  >
                    Stripe Dashboard
                  </button>
                  {' '}→ Payments → Select payment → Refund. Or go to{' '}
                  <strong>Financials → Overview</strong> in the sidebar.
                </p>
              </div>
            </div>
          )}

          {/* Notify customer */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="notify-email"
              checked={cancelNotifyEmail}
              onChange={e => setCancelNotifyEmail(e.target.checked)}
              className="w-4 h-4 accent-blue-600"
              data-testid="notify-customer-toggle"
            />
            <label htmlFor="notify-email" className="text-sm text-gray-700 cursor-pointer">
              Send cancellation email to customer
            </label>
          </div>

          {/* Optional reason */}
          <div>
            <Label className="text-sm text-gray-600 mb-1.5 block">Reason (optional)</Label>
            <Textarea
              placeholder="e.g. Customer no-show, weather, rescheduled..."
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              className="resize-none h-20 text-sm"
              data-testid="cancel-reason-input"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowCancelDialog(false)} disabled={cancelProcessing}>
            Go Back
          </Button>
          <Button
            onClick={handleCancelBooking}
            disabled={cancelProcessing}
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            data-testid="confirm-cancel-booking-btn"
          >
            {cancelProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Cancel Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ── DELETE RECORD DIALOG ───────────────────────────────── */}
    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Delete Record
          </DialogTitle>
          <DialogDescription>
            Permanently removes this booking from the database. No refund is issued and no email is sent.
            Use this for test data, duplicates, or junk entries.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <strong>This cannot be undone.</strong> If a payment was taken, cancel the booking and issue a refund first.
        </div>

        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          <span className="text-gray-500">Booking: </span>
          <span className="font-mono font-medium">{trip?.confirmationCode || trip?.confirmation_code}</span>
          <span className="text-gray-400 mx-2">·</span>
          <span className="text-gray-700">{trip?.customerName || trip?.customer_name}</span>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleteProcessing}>
            Go Back
          </Button>
          <Button
            onClick={handleDeleteBooking}
            disabled={deleteProcessing}
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            data-testid="confirm-delete-booking-btn"
          >
            {deleteProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete Permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    </>
  );
};

export default EditBookingModal;
